
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Button, Form, Typography, DatePicker, Space, Input } from 'antd';
import { FileTextFilled } from '@ant-design/icons';
import MainHeader, { MENU_INTERFACE_AX } from '../../components/common/MainHeader';
import { URL_INTERFACE_AX } from '../../constance/urls';
import { useTranslation } from 'react-i18next';
import { formatComma } from '../../utils/stringFormat';
import { POST } from '../../utils/HttpClient';
import { Toast } from '../../utils/Toast';
import * as _ from 'lodash'
import InterfaceAXTransactionModal from '../../components/interface-ax/InterfaceAXTransactionModal';
import TSTable from '../../components/common/TSTable';
import { DATE_FORMAT, HUMAN_DATE_FORMAT } from '../../constance/strings';
import { useDebounce } from 'use-debounce';
import dayjs from 'dayjs';
import ColorButton from '../../components/common/ColorButton';
import { BLUE_COLOR } from '../../constance/color';

function InterfaceAXLogWoodPOView() {
  const { t } = useTranslation();
  const [target, setTarget] = useState(null);
  const tableRef = useRef();
  const { Title } = Typography;
  const [form] = Form.useForm();
  const date = Form.useWatch('date', form);
  const searchText = Form.useWatch('search', form);
  const [debouncedText] = useDebounce(searchText, 500);
  const [isLoading, setIsLoading] = useState(false);
  const formattedDate = date ? dayjs(date).format(DATE_FORMAT) : null;

  const handleDoInterfaceAX = async (id) => {
    if (!tableRef.current) return;
    try {
      const response = await POST(`${URL_INTERFACE_AX.LOG_WOOD_PO}${id}/do_interface/`);
      tableRef.current.fetch();
      Toast.success(t('success'));
    } catch (error) {
      tableRef.current.setErrorMessages(error?.errorMessages || [t('error.occurred')]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.fetch();
    }
  }, [date, debouncedText]);


  const columns = useMemo(() => [
    {
      title: t('truck_transactions.in_date_time'),
      dataIndex: 'truck_transaction.in_date_time',
      render: (_, row) => row?.truck_transaction?.in_date_time
        ? new Date(row.truck_transaction.in_date_time).toLocaleString('en-GB')
        : '-'
    },
    {
      title: t('truck_transactions.out_date_time'),
      dataIndex: 'truck_transaction.out_date_time',
      render: (_, row) => row?.truck_transaction?.out_date_time
        ? new Date(row.truck_transaction.out_date_time).toLocaleString('en-GB')
        : <div style={{ textAlign: 'center' }}>-</div>
    },
    {
      title: t('trucks.license_plate'),
      dataIndex: 'truck_transaction.license_plate',
      render: (_, row) => row?.truck_transaction?.license_plate || '-'
    },
    {
      title: t('truck_transactions.company'),
      dataIndex: 'truck_transaction.truck_company_detail.name',
      render: (_, row) => row?.truck_transaction?.truck_company_detail?.name || '-'
    },
    {
      title: t('truck_transactions.in_weight'),
      dataIndex: 'truck_transaction.in_weight',
      render: (_, row) => formatComma(row?.truck_transaction?.in_weight) || '-'
    },
    {
      title: t('truck_transactions.out_weight'),
      dataIndex: 'truck_transaction.out_weight',
      render: (_, row) => formatComma(row?.truck_transaction?.out_weight) || '-'
    },
    {
      title: t('truck_transactions.net_weight'),
      dataIndex: 'truck_transaction.net_weight',
      render: (_, row) => formatComma(row?.truck_transaction?.net_weight) || '-'
    },
    {
      title: t('log_wood_evaluation_datas.cut_weight'),
      dataIndex: 'truck_transaction.cut_weight',
      render: (_, row) => formatComma(row?.truck_transaction?.cut_weight) || '-'
    },
    {
      title: t('log_wood_evaluation_datas.buy_price'),
      dataIndex: 'truck_transaction.buy_price',
      render: (_, row) => formatComma(row?.truck_transaction?.buy_price) || '-'
    },
    {
      title: t('log_wood_evaluation_datas.buy_amount'),
      dataIndex: 'truck_transaction.buy_amount',
      render: (_, row) => formatComma(row?.truck_transaction?.buy_amount) || '-'
    },
    {
      title: () => null,
      dataIndex: 'id',
      render: (__, original) => {

        const target = _.get(original, 'id', null);
        const progressing = _.get(original, 'interface_progressing', false);
        return (
          <Space>
            <ColorButton
              variant="outlined"
              color='primary'
              override={BLUE_COLOR}
              disabled={progressing || isLoading}
              onClick={() => !progressing && handleDoInterfaceAX(target)}
            >
              {progressing || isLoading ? t('progressing') : t('do_interface_ax')}
            </ColorButton>

            <ColorButton
              variant="outlined"
              color='primary'
              override={BLUE_COLOR}
              icon={<FileTextFilled />}
              onClick={() => setTarget(target)}
            />
          </Space>
        );
      },
    },
  ], [t]);


  return (
    <div>
      <MainHeader activeItem={MENU_INTERFACE_AX} />
      <div style={{ margin: '24px', marginTop: '7em' }}>
        <Title level={2}>
          {t('menus.interface_ax')}: {t('menus.interface_ax_menus.log_wood_po')}
        </Title>

        <Form form={form} layout="inline">
          <Form.Item label={t('searchs.fields.date')} name="date">
            <DatePicker
              format={HUMAN_DATE_FORMAT}
              placeholder={t('searchs.fields.date')}
              style={{ width: 200 }}
            />
          </Form.Item>
          <Form.Item label={t('search')} name="search">
            <Input
              placeholder={t('search')}
              allowClear
            />
          </Form.Item>
        </Form>

        <TSTable
          style={{ marginTop: '24px' }}
          ref={tableRef}
          url={URL_INTERFACE_AX.LOG_WOOD_PO}
          columns={columns}
          params={{
            date: formattedDate,
            search: searchText,
          }}
        />
        <InterfaceAXTransactionModal
          open={!!target}
          onClose={() => setTarget(null)}
          onInterfaceAX={handleDoInterfaceAX}
          url={URL_INTERFACE_AX.LOG_WOOD_PO}
          target={target}
        />
      </div>
    </div>
  );
}

export default InterfaceAXLogWoodPOView;
